import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {faCircleInfo, faTrash} from '@fortawesome/pro-duotone-svg-icons';
import {ButtonActionEnum, ButtonTypeEnum, IspColoursEnum} from '@i-supplier/angular-shared-module';
import {TranslateService} from '@ngx-translate/core';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {Subscription} from 'rxjs';

@Component({
  selector: 'app-modal-confirm',
  templateUrl: './modal-confirm.component.html',
  styleUrls: ['./modal-confirm.component.scss']
})
export class ModalConfirmComponent implements OnInit, OnDestroy {

  constructor(
    public modalRef: BsModalRef,
    private readonly _formBuilder: FormBuilder,
    private readonly translateService: TranslateService
  ) {
    this.commentForm = this._formBuilder.group({
      rejectReasons: [null],
      language: [{value: null, disabled: true}],
      comment: [null, [Validators.maxLength(this.commentMaxLength), Validators.minLength(this.commentMinLength)]],
      additionalComment: [null, [Validators.maxLength(this.additionalCommentMaxLength), Validators.minLength(this.additionalCommentMinLength)]],
    });
  }
  // Input variables
  placeholder: string;
  additionalPlaceholder: string;
  title: string;
  message: string;
  additionalMessage: string;
  confirmButtonLabel: string;
  rejectReasons: boolean = false;
  commentAvailable: boolean = false;
  commentRequired: boolean = false;
  commentMaxLength: number = 255;
  commentMinLength: number = 0;
  additionalCommentAvailable: boolean = false;
  additionalCommentRequired: boolean = false;
  additionalCommentMaxLength: number = 255;
  additionalCommentMinLength: number = 0;
  rejectReasonsData: {lang: string, list: {label: string, value: string}[], documentType: string};
  optionDelete: boolean = false;
  optionDeleteText: string = null;
  patchData: any = null;

  // Output variables
  @Output() actionConfirmed = new EventEmitter<any>();
  @Output() actionCanceled = new EventEmitter();
  @Output() actionDeleted = new EventEmitter();

  // Internal variables
  commentForm: FormGroup;
  loading: boolean = false;
  languages: Array<string>;
  rejectReasonsList: any;
  subscriptions: Subscription[] = [];
  additionalCommentTooltip: string;
  commentTooltip: string;
  protected readonly ButtonActionEnum = ButtonActionEnum;
  protected readonly ButtonTypeEnum = ButtonTypeEnum;
  protected readonly faTrash = faTrash;
  protected readonly IspColoursEnum = IspColoursEnum;

  protected readonly faCircleInfo = faCircleInfo;

  ngOnInit(): void {
    if (this.commentRequired) {
      this.commentAvailable = true;
    }
    if (this.additionalCommentRequired) {
      this.additionalCommentAvailable = true;
    }
    if (this.patchData) {
      this.commentForm.get('comment').setValue(this.patchData);
    }
    this.setRejectReasonsLogic();
    this.setCommentForValidation();
  }

  private setCommentForValidation() {
    if (this.commentAvailable) {
      if (this.commentRequired) {
        this.commentForm.get('comment').setValidators([Validators.required, Validators.maxLength(this.commentMaxLength), Validators.minLength(this.commentMinLength)]);
      } else {
        this.commentForm.get('comment').setValidators([Validators.maxLength(this.commentMaxLength), Validators.minLength(this.commentMinLength)]);
      }
      this.commentForm.get('comment').updateValueAndValidity();
    }
    if (this.additionalCommentAvailable) {
      if (this.additionalCommentRequired) {
        this.commentForm.get('additionalComment').setValidators([Validators.required, Validators.maxLength(this.additionalCommentMaxLength), Validators.minLength(this.additionalCommentMinLength)]);
      } else {
        this.commentForm.get('additionalComment').setValidators([Validators.maxLength(this.additionalCommentMaxLength), Validators.minLength(this.additionalCommentMinLength)]);
      }
      this.commentForm.get('additionalComment').updateValueAndValidity();
    }
  }

  confirmAction(): void {
    if (this.loading) {
      return;
    }
    this.loading = true;
    if (this.commentAvailable && this.additionalCommentAvailable) {
      this.actionConfirmed.emit({
        'comment': this.commentForm.get('comment')?.value?.trim(),
        'additionalComment': this.commentForm.get('additionalComment')?.value?.trim(),
      });
    } else if (this.commentAvailable) {
      this.actionConfirmed.emit(this.commentForm.get('comment')?.value?.trim());
    } else {
      this.actionConfirmed.emit();
    }
    this.modalRef.hide();
    setTimeout(() => {
      this.loading = false;
    }, 1000);
  }

  cancelAction(action: 'CANCEL'|'DELETE'): void {
    if (this.loading) {
      return;
    }
    this.loading = true;
    (action === 'CANCEL' ? this.actionCanceled : this.actionDeleted).emit();
    this.modalRef.hide();
    setTimeout(() => {
      this.loading = false;
    }, 1000);
  }

  private setRejectReasonsLogic() {
    if (this.rejectReasons) {
      this.languages = this.translateService.getLangs();
      this.rejectReasonsList = this.rejectReasonsData.list;

      if (this.rejectReasonsData.lang === 'FR') {
        this.commentForm.get('language').setValue(this.rejectReasonsData.lang);
      }

      this.subscriptions.push(this.commentForm.get('rejectReasons').valueChanges.subscribe(() => {
        this.commentForm.get('language').enable();
        if (this.commentForm.get('language').value) {
          this.setSpecificLangReason(this.commentForm.get('language').value, this.rejectReasonsData.documentType);
        }
      }));
      this.subscriptions.push(this.commentForm.get('language').valueChanges.subscribe(lang => {
        if (lang) {this.setSpecificLangReason(lang, this.rejectReasonsData.documentType); }
      }));
    }
  }

  setSpecificLangReason(lang: string, documentType: string) {
    this.subscriptions.push(this.translateService.getTranslation(lang).subscribe((translations) => {
      const rejectionReason = translations['legal-documents']['rejection-list'][this.commentForm.get('rejectReasons').value]['label'];
      const document = translations['core']['components']['breadcrumb'][documentType];
      this.commentForm.get('comment').setValue(rejectionReason.replace('{{document}}', document));
    }));
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }
}
